import { FC, useState } from "react"

import Table from "../table"
import BBModal from "../modal"
import BrandAutocomplete from "../brand-autocomplete"
import MemberCell from "./cells/member-cell"
import { Button, PrimaryBtn } from "../buttons"
import { useImportLoMemberMutation } from "../../../redux/queries"
import { pushToast } from "../../../redux/slices/notifications"
import { useAppDispatch } from "../../hooks"
import { BBColumnDef, Brand, LoanOfficer, LoUploadStatus } from "../../../types"

interface Props {
  rows: LoanOfficer[]
  show: boolean
  brands: Brand[]
  onClose: () => void
}

const ImportMembersModal: FC<Props> = ({ show, rows, brands, onClose }) => {
  const dispatch = useAppDispatch()

  const [importMember] = useImportLoMemberMutation()

  const [selectedBrandId, setSelectedBrandId] = useState<string>("")
  const [rowStatus, setRowStatus] = useState<Record<number, LoUploadStatus>>({})

  const importRows = async () => {
    if (!selectedBrandId) return

    const newStatus: Record<number, LoUploadStatus> = {}

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      newStatus[i] = "pending"
      setRowStatus({ ...newStatus })

      try {
        await importMember({ brand_id: selectedBrandId, row }).unwrap()
        newStatus[i] = "success"
      } catch (e) {
        newStatus[i] = "error"
      }

      setRowStatus({ ...newStatus })
    }

    if (Object.values(newStatus).every(status => status === "success")) {
      dispatch(
        pushToast({
          msg: "Successfully imported LOs",
          timeout: 3000,
          theme: "info"
        })
      )
      onClose()
    } else {
      dispatch(
        pushToast({
          msg: "Failed to import some LOs",
          timeout: 3000,
          theme: "danger"
        })
      )
    }
  }

  const columns: BBColumnDef<LoanOfficer>[] = [
    {
      id: "first_name",
      header: "First Name",
      accessorFn: row => row.first_name,
      footer: props => props.column.id,
      cell: props => (
        <MemberCell
          status={rowStatus[props.row.index]}
          value={props.row.original.first_name}
          className="font-bold py-4 pl-2 pr-3"
        />
      )
    },
    {
      id: "last_name",
      header: "Last Name",
      accessorFn: row => row.last_name,
      footer: props => props.column.id,
      cell: props => (
        <MemberCell
          status={rowStatus[props.row.index]}
          value={props.row.original.last_name}
          className="font-bold py-4 px-3"
        />
      )
    },
    {
      id: "nmls",
      header: "NMLS",
      accessorFn: row => row.nmls,
      footer: props => props.column.id,
      cell: props => (
        <MemberCell
          status={rowStatus[props.row.index]}
          value={props.row.original.nmls}
        />
      )
    },
    {
      id: "email",
      header: "Email",
      accessorFn: row => row.email,
      footer: props => props.column.id,
      cell: props => (
        <MemberCell
          status={rowStatus[props.row.index]}
          value={props.row.original.email}
        />
      )
    }
  ]

  return (
    <BBModal
      show={show}
      title="Import Loan Officers"
      onClose={onClose}
      size="2xl"
    >
      <div className="pb-8 z-100">
        <BrandAutocomplete
          brands={brands}
          selected={selectedBrandId}
          onChange={setSelectedBrandId}
        />
      </div>
      <div className="py-4">
        <Table
          data={rows}
          columns={columns}
          viewType="table"
          searchPlaceholder="Search LO's by name, email or brand…"
          shrinkFilterSort
        />
      </div>

      <div className="flex justify-end pt-6">
        <Button className="mr-3" onClick={onClose}>
          Close
        </Button>
        <PrimaryBtn
          disabled={!selectedBrandId || !rows.length}
          onClick={importRows}
        >
          Import
        </PrimaryBtn>
      </div>
    </BBModal>
  )
}

export default ImportMembersModal
